import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";

const API_BASE_URL = "https://admin.shopaholics.ge/api";

/* ===== PRESETS AS IS ===== */
const brandBackgrounds = {
  "orto parisi": "/images/orto-parisi-bg.jpg",
  "creed": "/images/creed-bg.jpg",
  "tom ford": "/images/tom-ford-bg.jpg",
  "dior": "/images/dior-bg.jpg",
  "chanel": "/images/chanel-bg.jpg",
  "acqua di parma": "/images/acqua-di-parma-bg.jpg",
  "afnan": "/images/afnan-bg.webp",
  "al haramain": "/images/al-haramain-bg.jpg",
  "amouage": "/images/amouage-bg.webp",
  "argos": "/images/argos-bg.jpg",
  "armaf": "/images/armaf-bg.webp",
  "azzaro": "/images/azzaro-bg.jpg",
  "bdk parfums": "/images/bdk-parfums-bg.jpeg",
  "boadicea the victorious": "/images/Boadicea-the-Victorious-bg.jpg",
  "bvlgari": "/images/Bvlgari-bg.jpg",
  "by kilian": "/images/By-Kilian-bg.jpg",
  "byredo": "/images/byredo-bg.jpg",
  "carolina herrera": "/images/carolina-Herrera-bg.jpg",
  "clive christian": "/images/clive-christian-bg.jpg",
  "dolce & gabbana": "/images/dolce-gabana-bg.jpg",
  "escentric molecules": "/images/escentric-molecules-bg.jpg",
  "essential parfums": "/images/essential-parfums-bg.jpg",
  "ex nihilo": "/images/ex-nihilo-bg.jpg",
  "frederic malle": "/images/frederic-malle-bg.jpg",
  "giorgio armani": "/images/giorgio-armani-bg.jpg",
  "givenchy": "/images/givenchy-bg.avif",
  "goldfield & banks": "/images/goldfield-&-banks-bg.webp",
  "gritti": "/images/gritti-bg.jpg",
  "hermès": "/images/hermès-bg.jpg",
  "histoires de parfums": "/images/histoires-de-parfums-bg.webp",
  "initio": "/images/initio-bg.webp",
  "jean paul gaultier": "/images/jean-paul-gaultier-bg.jpg",
  "jo malone": "/images/jo-malone-bg.jpg",
  "juliette has a gun": "/images/juliette-has-a-gun.webp",
};

const normalize = (s) => (s || "").toLowerCase().trim();
const getPresetBg = (name) => brandBackgrounds[normalize(name)];
const getBgSource = (brand) => {
  if (getPresetBg(brand.name)) return "preset";
  if (brand.background_url) return "uploaded";
  return "missing";
};
const getEffectiveBg = (brand) =>
  getPresetBg(brand.name) || brand.background_url || "";

/* ===== THEME ===== */
const UI = {
  pageBg: "#f5f7fb",
  cardBg: "#ffffff",
  text: "#0f172a",
  dim: "#64748b",
  line: "rgba(2,6,23,.08)",
  lineStrong: "rgba(2,6,23,.12)",
  focus: "#2563eb",
  primary: "#0ea5e9",
  radius: "14px",
  shadow: "0 10px 28px rgba(2,6,23,.06)",
};

const Page = styled.div`
  min-height: 100vh;
  background: ${UI.pageBg};
  padding: clamp(24px, 4vw, 48px) 16px;
`;

const Container = styled.div`
  max-width: 1100px;
  margin-inline: auto;
`;

const Header = styled.div`
  display: grid;
  gap: 6px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: clamp(20px, 2.4vw, 24px);
  font-weight: 800;
  letter-spacing: .2px;
  color: ${UI.text};
  margin: 0;
`;

const Message = styled.div`
  margin-top: 6px;
  font-weight: 600;
  color: ${({ ok }) => (ok ? "#16a34a" : "#dc2626")};
`;

const Toolbar = styled.div`
  position: sticky; top: 0; z-index: 5;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  padding: 12px;
  margin: 14px 0 18px;
  background: ${UI.cardBg};
  border: 1px solid ${UI.line};
  border-radius: ${UI.radius};
  box-shadow: ${UI.shadow};
`;

const Search = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 15px;
  border: 1px solid ${UI.line};
  border-radius: 12px;
  outline: none;
  transition: box-shadow .15s ease, border-color .15s ease;
  &:focus {
    border-color: ${UI.focus};
    box-shadow: 0 0 0 3px rgba(37,99,235,.15);
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const Checkbox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${UI.line};
  border-radius: 12px;
  background: #f8fbff;
  font-size: 13px;
  color: ${UI.text};
  input { transform: translateY(1px); }
`;

const AlphaRow = styled.div`
  display: flex; flex-wrap: wrap;
  gap: 6px; margin: 10px 0 16px;
`;

const AlphaBtn = styled.button`
  padding: 8px 10px;
  border: 1px solid ${UI.line};
  background: #fff;
  border-radius: 10px;
  font-weight: 700; font-size: 13px; color: ${UI.text};
  cursor: pointer; transition: background .15s ease, border-color .15s ease;
  &:hover { background: #f2f6fd; border-color: ${UI.lineStrong}; }
`;

const List = styled.ul`
  list-style: none; padding: 0; margin: 0;
  display: grid; gap: 12px;
`;

const Item = styled.li`
  background: ${UI.cardBg};
  border: 1px solid ${UI.line};
  border-radius: ${UI.radius};
  box-shadow: ${UI.shadow};
  padding: 14px;
  display: grid;
  grid-template-columns: 210px 1fr auto;
  gap: 16px;
  align-items: start;
  @media (max-width: 920px) { grid-template-columns: 1fr; }
`;

const Thumbs = styled.div`
  display: grid; gap: 8px;
`;

const ThumbTitle = styled.div`
  font-size: 12.5px; color: ${UI.dim}; font-weight: 700; letter-spacing: .3px;
`;

const Img = styled.img`
  width: 200px; height: 120px; object-fit: cover;
  border-radius: 12px; border: 1px solid ${UI.line};
  background: #fff;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  margin-left: 6px;
  background: ${({ $s }) =>
    $s === "preset" ? "#2e7d32" :
    $s === "uploaded" ? "#1d4ed8" : "#b91c1c"};
`;

const Name = styled.div`
  font-weight: 800; color: ${UI.text}; font-size: 16px; margin-bottom: 10px;
`;

const EditGrid = styled.div`
  display: grid; gap: 12px 16px; grid-template-columns: 1fr 1fr;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const Input = styled.input`
  padding: 10px 12px; border: 1px solid ${UI.line}; border-radius: 10px; font-size: 15px;
  &:focus { border-color: ${UI.focus}; box-shadow: 0 0 0 3px rgba(37,99,235,.15); outline: none; }
`;

const Textarea = styled.textarea`
  padding: 10px 12px; min-height: 90px; border: 1px solid ${UI.line}; border-radius: 10px; font-size: 15px;
  grid-column: span 2; outline: none;
  &:focus { border-color: ${UI.focus}; box-shadow: 0 0 0 3px rgba(37,99,235,.15); }
  @media (max-width: 720px) { grid-column: span 1; }
`;

const FileBlock = styled.div` display: grid; gap: 6px; `;
const FileLabel = styled.label` font-weight: 700; font-size: 13px; color: ${UI.text}; `;
const FileInput = styled.input``;

const Btns = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap; align-items: center; justify-content: flex-end;
`;

const Btn = styled.button`
  appearance: none; border: 1px solid transparent;
  border-radius: 10px; padding: 10px 14px; font-weight: 800; cursor: pointer; font-size: 14px;
  transition: transform .12s ease, background .15s ease, border-color .15s ease;

  ${({ typex }) => {
    switch (typex) {
      case "edit": return `
        color:#fff; background:#2563eb;
        &:hover{ background:#1d4ed8; } &:active{ transform: translateY(1px); }
      `;
      case "save": return `
        color:#fff; background:#059669;
        &:hover{ background:#047857; } &:active{ transform: translateY(1px); }
      `;
      case "cancel": return `
        color:#0f172a; background:#eef2ff; border-color:${UI.line};
        &:hover{ background:#e2e8ff; } &:active{ transform: translateY(1px); }
      `;
      case "delete": return `
        color:#fff; background:#dc2626;
        &:hover{ background:#b91c1c; } &:active{ transform: translateY(1px); }
      `;
      default: return `
        color:#0f172a; background:#f1f5f9; border-color:${UI.line};
        &:hover{ background:#e8eef6; } &:active{ transform: translateY(1px); }
      `;
    }
  }}
`;

const ManageBrands = () => {
  const [brands, setBrands] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingBrand, setEditingBrand] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState("");
  const [message, setMessage] = useState("");

  const [newImage, setNewImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [newBackground, setNewBackground] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);

  const [onlyMissing, setOnlyMissing] = useState(false);
  const [onlyPreset, setOnlyPreset] = useState(false);

  useEffect(() => { fetchBrands(); }, []);

  const fetchBrands = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/get_brands.php`);
      setBrands(data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      console.error("Error fetching brands:", e);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    setNewImage(file || null);
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleBackgroundChange = (e) => {
    const file = e.target.files?.[0];
    setNewBackground(file || null);
    setBgPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this brand?")) return;
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser || !storedUser.email) { setMessage("Unauthorized. Please log in."); return; }

    try {
      await axios.post(`${API_BASE_URL}/delete_brand.php`, { id, email: storedUser.email });
      setBrands(prev => prev.filter((b) => b.id !== id));
      setMessage("Brand deleted successfully");
    } catch (e) {
      setMessage("Error deleting brand");
      console.error(e);
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setPreview(brand.image_url || null);
    setBgPreview(brand.background_url || getPresetBg(brand.name) || null);
    setNewImage(null);
    setNewBackground(null);
    setMessage("");
  };

  const handleSave = async () => {
    if (!editingBrand) return;
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser || !storedUser.email) { setMessage("Unauthorized. Please log in."); return; }

    const formData = new FormData();
    formData.append("id", editingBrand.id);
    formData.append("name", editingBrand.name);
    formData.append("description_ge", editingBrand.description_ge || "");
    formData.append("description_en", editingBrand.description_en || "");
    formData.append("email", storedUser.email);
    if (newImage) formData.append("image", newImage);
    if (newBackground) formData.append("background", newBackground);

    try {
      const { data } = await axios.post(`${API_BASE_URL}/update_brand.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setBrands(prev =>
        prev.map(b =>
          b.id === editingBrand.id
            ? {
                ...editingBrand,
                image_url: newImage ? (data?.image_url || preview) : editingBrand.image_url,
                background_url: newBackground ? (data?.background_url || bgPreview) : editingBrand.background_url,
              }
            : b
        )
      );

      setEditingBrand(null);
      setNewImage(null); setPreview(null);
      setNewBackground(null); setBgPreview(null);
      setMessage("Brand updated successfully");
    } catch (e) {
      setMessage("Error updating brand");
      console.error(e);
    }
  };

  const filteredBrands = brands
    .filter((b) =>
      b.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (selectedLetter === "" || b.name.toUpperCase().startsWith(selectedLetter))
    )
    .filter((b) => {
      const source = getBgSource(b);
      if (onlyMissing && source !== "missing") return false;
      if (onlyPreset && source !== "preset") return false;
      return true;
    });

  const ok = message.toLowerCase().includes("success");

  return (
    <Page>
      <Container>
        <Header>
          <Title>Manage Brands</Title>
          {message && <Message ok={ok}>{message}</Message>}
        </Header>

        <Toolbar>
          <Search
            type="text"
            placeholder="Search brands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <FiltersRow>
            <Checkbox>
              <input
                type="checkbox"
                checked={onlyMissing}
                onChange={(e) => setOnlyMissing(e.target.checked)}
              />
              Only missing
            </Checkbox>
            <Checkbox>
              <input
                type="checkbox"
                checked={onlyPreset}
                onChange={(e) => setOnlyPreset(e.target.checked)}
              />
              Only preset
            </Checkbox>
          </FiltersRow>
        </Toolbar>

        <AlphaRow>
          {Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ").map((letter) => (
            <AlphaBtn key={letter} onClick={() => setSelectedLetter(letter)}>{letter}</AlphaBtn>
          ))}
          <AlphaBtn onClick={() => setSelectedLetter("")}>All</AlphaBtn>
        </AlphaRow>

        <List>
          {filteredBrands.length > 0 ? (
            filteredBrands.map((brand) => {
              const source = getBgSource(brand);
              const effective = getEffectiveBg(brand) || "/placeholder.jpg";

              return (
                <Item key={brand.id}>
                  {/* thumbs */}
                  <Thumbs>
                    <div>
                      <ThumbTitle>Logo</ThumbTitle>
                      <Img src={brand.image_url || "/placeholder.jpg"} alt={brand.name} />
                    </div>
                    <div>
                      <ThumbTitle>
                        Background
                        <Pill $s={source}>
                          {source === "preset" ? "Preset" : source === "uploaded" ? "Uploaded" : "Missing"}
                        </Pill>
                      </ThumbTitle>
                      <Img src={effective} alt={`${brand.name} bg`} />
                    </div>
                  </Thumbs>

                  {/* middle: content */}
                  {editingBrand?.id === brand.id ? (
                    <div>
                      <Name>Editing: {editingBrand.name}</Name>

                      <EditGrid>
                        <Input
                          type="text"
                          value={editingBrand.name}
                          onChange={(e) => setEditingBrand({ ...editingBrand, name: e.target.value })}
                        />

                        <div />

                        <Textarea
                          value={editingBrand.description_ge || ""}
                          onChange={(e) => setEditingBrand({ ...editingBrand, description_ge: e.target.value })}
                          placeholder="Description (Georgian)"
                        />
                        <Textarea
                          value={editingBrand.description_en || ""}
                          onChange={(e) => setEditingBrand({ ...editingBrand, description_en: e.target.value })}
                          placeholder="Description (English)"
                        />

                        <FileBlock>
                          <FileLabel>Update Logo (optional)</FileLabel>
                          <FileInput type="file" accept="image/*" onChange={handleImageChange} />
                          {preview && <Img src={preview} alt="Logo Preview" />}
                        </FileBlock>

                        <FileBlock>
                          <FileLabel>Update Background (optional)</FileLabel>
                          <FileInput type="file" accept="image/*" onChange={handleBackgroundChange} />
                          {bgPreview && <Img src={bgPreview} alt="Background Preview" />}
                        </FileBlock>
                      </EditGrid>
                    </div>
                  ) : (
                    <div>
                      <Name>{brand.name}</Name>
                      <div style={{ color: UI.dim, fontSize: 13 }}>
                        {brand.description_en?.slice(0, 160) || "—"}
                        {brand.description_en && brand.description_en.length > 160 ? "…" : ""}
                      </div>
                    </div>
                  )}

                  {/* right: actions */}
                  <Btns>
                    {editingBrand?.id === brand.id ? (
                      <>
                        <Btn typex="save" onClick={handleSave}>Save</Btn>
                        <Btn typex="cancel" onClick={() => {
                          setEditingBrand(null);
                          setNewImage(null); setPreview(null);
                          setNewBackground(null); setBgPreview(null);
                        }}>Cancel</Btn>
                      </>
                    ) : (
                      <>
                        <Btn typex="edit" onClick={() => handleEdit(brand)}>Edit</Btn>
                        <Btn typex="delete" onClick={() => handleDelete(brand.id)}>Delete</Btn>
                      </>
                    )}
                  </Btns>
                </Item>
              );
            })
          ) : (
            <div style={{ color: UI.dim, padding: "8px 2px" }}>No brands found.</div>
          )}
        </List>
      </Container>
    </Page>
  );
};

export default ManageBrands;
